import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getWaitlistById, updateWaitlistEntry, updateWaitlistStatus, seatWaitlistEntry, getTables, getCustomerWaitlistHistory } from "../../../utils/apiCalls";
import { errorToast } from "../../../shared/Toast";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const CANCEL_REASONS = [
  { value: "customer_left", label: "Customer left" },
  { value: "staff_cancelled", label: "Staff cancelled" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "duplicate", label: "Duplicate" },
  { value: "other", label: "Other" },
];

const statusBadge = (s) => {
  const map = { waiting: { color: C.orange, bg: C.orangeLight }, notified: { color: C.blue, bg: C.blueLight }, seated: { color: C.green, bg: C.greenLight }, cancelled: { color: C.textLight, bg: C.borderLight }, no_show: { color: C.coral, bg: C.coralLight } };
  return map[s] || { color: C.textMed, bg: C.borderLight };
};

const fieldStyle = { borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 };

const EditWaitlist = () => {
  const { waitlistId } = useParams();
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = (userType === "merchant" || userType === "waitlist_staff") ? merchantId : reduxSelectedMerchant;

  const [entry, setEntry] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [history, setHistory] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("customer_left");

  useEffect(() => {
    const load = async () => {
      const data = await getWaitlistById(waitlistId);
      if (data) {
        setEntry(data);
        setForm({ customerName: data.customerName || "", customerPhone: data.customerPhone || "", partySize: data.partySize || 1, quotedWaitMinutes: data.quotedWaitMinutes || "", notes: data.notes || "" });
        if (data.branchId) { const t = await getTables({ branchId: data.branchId }); setTables(t || []); }
        if (data.customerPhone && selectedMerchant && selectedMerchant !== "all") {
          const h = await getCustomerWaitlistHistory({ MerchantId: selectedMerchant, phone: data.customerPhone });
          setHistory(h);
        }
      }
      setLoading(false);
    };
    load();
  }, [waitlistId, selectedMerchant]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...form, partySize: Number(form.partySize) };
    if (payload.quotedWaitMinutes) payload.quotedWaitMinutes = Number(payload.quotedWaitMinutes);
    const result = await updateWaitlistEntry(waitlistId, payload);
    if (result) setEntry((prev) => ({ ...prev, ...result }));
    setSaving(false);
  };

  const handleSeat = async () => {
    if (!selectedTableId) { errorToast("Please select a table"); return; }
    const result = await seatWaitlistEntry(waitlistId, { tableId: Number(selectedTableId) });
    if (result) setEntry((prev) => ({ ...prev, ...result }));
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "cancelled") {
      setCancelReason("customer_left");
      setCancelModalOpen(true);
      return;
    }
    const result = await updateWaitlistStatus(waitlistId, { status: newStatus });
    if (result) setEntry((prev) => ({ ...prev, ...result }));
  };

  const handleConfirmCancel = async () => {
    const result = await updateWaitlistStatus(waitlistId, { status: "cancelled", cancelReason });
    if (result) {
      setEntry((prev) => ({ ...prev, ...result }));
      setCancelModalOpen(false);
    }
  };

  if (loading) return <Fragment><Breadcrumb title="Edit Waitlist" parent="Table Management" /><Container fluid><Loader /></Container></Fragment>;
  if (!entry) return <Fragment><Breadcrumb title="Edit Waitlist" parent="Table Management" /><Container fluid><RCard><p style={{ textAlign: "center", color: C.textLight }}>Entry not found</p></RCard></Container></Fragment>;

  const canSeat = entry.status === "waiting" || entry.status === "notified";
  const canNotify = entry.status === "waiting";
  const canCancel = entry.status === "waiting" || entry.status === "notified";
  const isTerminal = ["seated", "cancelled", "no_show"].includes(entry.status);
  const sb = statusBadge(entry.status);

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Edit Waitlist Entry" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        {/* Customer History */}
        {history && history.totalVisits > 1 && (
          <RCard style={{ marginBottom: 16, background: history.noShowCount >= 2 ? C.coralLight : C.borderLight }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                Returning customer
              </span>
              <span style={{ fontSize: 12, color: C.textMed }}>
                {history.totalVisits} visits (last 90 days)
              </span>
              <span style={{ fontSize: 12, color: C.textMed }}>
                {history.seatedCount} seated
              </span>
              {history.noShowCount > 0 && (
                <Badge color={C.coral} bg={C.coralLight}>
                  {history.noShowCount} no-show{history.noShowCount > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
          </RCard>
        )}

        {/* Status + Actions */}
        <RCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Status:</span>
              <Badge color={sb.color} bg={sb.bg}>{entry.status}</Badge>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {canSeat && (
                <>
                  <Input type="select" style={{ ...fieldStyle, maxWidth: 200 }} value={selectedTableId} onChange={(e) => setSelectedTableId(e.target.value)}>
                    <option value="">Select Table</option>
                    {tables.filter((t) => t.status === "available").map((t) => <option key={t.id} value={t.id}>{t.name} (#{t.tableNumber})</option>)}
                  </Input>
                  <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={handleSeat}>Seat</Button>
                </>
              )}
              {canNotify && <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={() => handleStatusChange("notified")}>Notify</Button>}
              {canCancel && <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.coral, fontWeight: 600, fontSize: 12 }} onClick={() => handleStatusChange("cancelled")}>Cancel</Button>}
            </div>
          </div>
        </RCard>

        {/* Edit Form */}
        <RCard>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <Label style={labelStyle}>Customer Name</Label>
              <Input value={form.customerName} onChange={(e) => handleChange("customerName", e.target.value)} disabled={isTerminal} style={fieldStyle} />
            </div>
            <div>
              <Label style={labelStyle}>Phone</Label>
              <Input value={form.customerPhone} onChange={(e) => handleChange("customerPhone", e.target.value)} disabled={isTerminal} style={fieldStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
            <div>
              <Label style={labelStyle}>Party Size</Label>
              <Input type="number" min="1" value={form.partySize} onChange={(e) => handleChange("partySize", e.target.value)} disabled={isTerminal} style={fieldStyle} />
            </div>
            <div>
              <Label style={labelStyle}>Estimated Wait (min)</Label>
              <Input type="number" value={form.quotedWaitMinutes} onChange={(e) => handleChange("quotedWaitMinutes", e.target.value)} disabled={isTerminal} style={fieldStyle} />
            </div>
            <div>
              <Label style={labelStyle}>Notes</Label>
              <Input value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} disabled={isTerminal} style={fieldStyle} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
            <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => navigate("/table-management/waitlist")}>Back</Button>
            {!isTerminal && (
              <Button style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </RCard>
      </Container>

      <Modal isOpen={cancelModalOpen} toggle={() => setCancelModalOpen(false)} centered>
        <ModalHeader toggle={() => setCancelModalOpen(false)}>Cancel waitlist entry</ModalHeader>
        <ModalBody>
          <p style={{ color: C.textMed, marginBottom: 12 }}>
            Cancel <strong>{entry?.customerName}</strong> (party of {entry?.partySize})?
          </p>
          <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Reason</Label>
          <Input type="select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} style={fieldStyle}>
            {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setCancelModalOpen(false)} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Keep</Button>
          <Button onClick={handleConfirmCancel} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", fontWeight: 600 }}>Confirm Cancel</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default EditWaitlist;
